import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface DateRange {
  from: Date;
  to: Date;
}

interface DateRangePickerProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
}

const DateRangePicker = ({
  dateRange,
  onDateRangeChange,
}: DateRangePickerProps) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDateRange = () => {
    if (dateRange.from && dateRange.to) {
      if (dateRange.from.getTime() === dateRange.to.getTime()) {
        return formatDate(dateRange.from);
      }
      return `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`;
    }
    return 'Select date range';
  };

  const quickRanges = [
    {
      label: 'Last 7 days',
      range: {
        from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        to: new Date(),
      },
    },
    {
      label: 'Last 30 days',
      range: {
        from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        to: new Date(),
      },
    },
    {
      label: 'Last 90 days',
      range: {
        from: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
        to: new Date(),
      },
    },
  ];

  // Helper function to check if a date range matches the current selection
  const isRangeActive = (quickRange: typeof quickRanges[0]) => {
    if (!dateRange.from || !dateRange.to) return false;
    
    // Calculate the difference in days for the current range
    const currentDays = Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (24 * 60 * 60 * 1000));
    
    // Calculate the difference in days for the quick range
    const quickDays = Math.ceil((quickRange.range.to.getTime() - quickRange.range.from.getTime()) / (24 * 60 * 60 * 1000));
    
    // Check if they match (within 1 day tolerance for timing differences)
    return Math.abs(currentDays - quickDays) <= 1;
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2">
      <Button
        variant="outline"
        className="justify-start text-left font-normal whitespace-nowrap text-sm sm:text-base"
      >
        <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
        <span className="truncate">{formatDateRange()}</span>
      </Button>
      <div className="flex flex-wrap gap-1 sm:gap-1">
        {quickRanges.map((quickRange) => {
          const isActive = isRangeActive(quickRange);
          return (
            <Button
              key={quickRange.label}
              variant={isActive ? "default" : "ghost"}
              size="sm"
              className={`text-xs transition-all whitespace-nowrap flex-shrink-0 ${
                isActive 
                  ? "bg-genie-primary text-white shadow-md hover:bg-[#3D8BFF]" 
                  : "text-gray-600 hover:text-genie-primary hover:bg-genie-primary/10"
              }`}
              onClick={() => onDateRangeChange(quickRange.range)}
            >
              {quickRange.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default DateRangePicker;